<script lang="ts" setup>
  import { CartLayout } from '@storefront/components';
  import { computed, type PropType } from 'vue';
  import { useRealizationTime } from '@storefront/storefront';
  import type { OrderResource, BillingOption, SharedOrder } from '@storefront/resources';
  import { type FormContextInterface, setModelWithContext } from '@othercommerce/foundation';
  import { BillingDriver } from '@othercommerce/domain';

  interface Order extends OrderResource {
    shippingName: string | null;
    billingName: string | null;
    hasCustomerNote: string | null;
    realizationFrom: number | null;
    realizationTo: number | null;
    billingMethodDriver: {
      value: BillingDriver;
      label: string;
    } | null;
  }

  defineOptions({
    layout: CartLayout,
    inheritAttrs: false,
  });

  const props = defineProps({
    order: { type: Object as PropType<Order>, required: true },
    repayment: { type: String, required: true },
    options: { type: Array as PropType<BillingOption[]>, required: true },
    isCeneoEnabled: { type: Boolean, required: true },
  });

  const realization = computed(() => useRealizationTime(props.order));

  function isSelected(option: BillingOption, data: SharedOrder) {
    return (
      data.billingMethodId === option.billingMethodId &&
      data.billingMethodOptionId === option.id
    );
  }

  function onChoose(option: BillingOption, ctx: FormContextInterface) {
    setModelWithContext('billingMethodId', ctx, option.billingMethodId);
    setModelWithContext('billingMethodOptionId', ctx, option.id);
  }
</script>
<template>
  <main id="main">
    <div class="order">
      <div class="payment-details">
        <FormController :data="order" :action="repayment" method="PATCH" v-slot="{ processing, submit, ctx, data }">
          <div class="payment-details__column payment-details__column--left">
            <SfButton :href="$route('web.homepage.index')" class="payment-details__home-btn">
              <template #before>
                <SfIconChevronLeft aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ORDER.ACTION.BACK_TO_SHOP') }}
              </template>
            </SfButton>
            <div class="payment-details__section">
              <h2 class="payment-details__section-title">
                {{ $t('SHOP.ORDER.HEADER.PAYMENT_METHOD') }}
              </h2>
              <div class="payment-details__section-content">
                <div class="billing-method-form">
                  <template v-for="option in options">
                    <SfListCard class="billing-method-form__option" interactive @click="onChoose(option, ctx)" @keydown.space="onChoose(option, ctx)" :selected="isSelected(option, data)" tabindex="0" role="button">
                      <header class="billing-method-form__header">
                        <img :src="option.icon" :alt="option.name" class="billing-method-form__image" />
                        <div class="billing-method-form__name-container">
                          <h3 class="billing-method-form__name">{{ option.name }}</h3>
                        </div>
                      </header>
                    </SfListCard>
                  </template>
                </div>
              </div>
            </div>
            <div class="payment-details__section">
              <h2 class="payment-details__section-title">
                {{ $t('SHOP.ORDER.HEADER.TERMS_AND_CONDITIONS') }}
              </h2>
              <div class="payment-details__section-content ">
                <OrderConditions :cart="order" :isCeneoEnabled />
              </div>
            </div>
          </div>
          <div class="payment-details__column payment-details__column--right">
            <SfTile class="payment-details__summary">
              <template #outer>
                <h2 class="order-heading">
                  {{ $t('SHOP.ORDER.HEADER.CART') }}
                </h2>
              </template>
              <div class="payment-details__products">
                <OrderSummaryItems v-for="item in order.items" :key="item.id" :item="item" />
              </div>
              <div class="payment-details__options-summary">
                <div class="shipping-option-summary" v-if="order.shippingOptionName">
                  <SfIconTruck class="shipping-option-summary__icon" />
                  <div class="shipping-option-summary__content">
                    <span class="shipping-option-summary__property">{{ $t('SHOP.ORDER.LABEL.SHIPPING_METHOD') }}</span>
                    <span class="shipping-option-summary__value">{{ order.shippingOptionName }}</span>
                  </div>
                </div>
                <div v-if="realization" class="realization-option-summary">
                  <SfIconClock class="realization-option-summary__icon" />
                  <div class="realization-option-summary__content">
                    <span class="realization-option-summary__property">{{ $t('SHOP.ORDER.LABEL.SHIPPING_ESTIMATED_DATE') }}</span>
                    <span class="realization-option-summary__value">{{ realization }}</span>
                  </div>
                </div>
              </div>
              <OrderSummary :order>
                <SfButton primary large :loading="processing" @click="submit">
                  <template #before>
                    <SfIconWallet aria-hidden="true" />
                  </template>
                  <template #default>
                    {{ $t('SHOP.ORDER.ACTION.PAY_FOR_ORDER') }}
                  </template>
                </SfButton>
              </OrderSummary>
            </SfTile>
          </div>
        </FormController>
      </div>
    </div>
  </main>

  <RouterView />
</template>
<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  .payment-details {
    display: grid;
    grid-template-columns: 2fr minmax(450px, 1fr);
    gap: 3rem;

    &__home-btn {
      align-self: flex-start;
    }

    &__section-content {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding: var(--sf-tile-padding);
      background: var(--sf-tile-background);
      border-inline: var(--sf-tile-border-inline);
      border-block: var(--sf-tile-border-block);
      border-radius: var(--section-border-radius);
    }

    &__section-title {
      padding: 1rem 0;
      font-weight: var(--font-weight-semi);
      line-height: 1.5;
      font-size: 1.25rem;
    }

    &__section-header {
      display: flex;
      align-items: center;
      column-gap: 0.625rem;
    }

    &__column--left {
      display: flex;
      flex-direction: column;
      gap: 2rem;
    }

    &__summary {
      --sf-tile-gap: 2rem;
      position: sticky;
      top: var(--sf-order-cart-summary-sticky-top);
    }

    &__products {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__options-summary {
      display: flex;
      flex-direction: column;
      border-radius: var(--sf-order-details-options-summary-border-radius);
      background-color: var(--sf-order-details-options-summary-bg);
    }

    &__option-summary {
      &:first-child {
        padding-top: 1rem;
      }

      &:last-child {
        padding-bottom: 1rem;
      }
    }
  }

  @include media.query(xl) {
    .payment-details {
      grid-template-columns: 1fr;
      margin: 0;
      gap: 1rem;

      &__column--right {
        margin-top: 1rem;
        position: static;
      }

      &__heading {
        margin-top: 20px;
      }
    }
  }

  @include media.query(lg) {
    .payment-details {
      margin: 0;
      gap: 0.625rem;

      &__home-btn {
        margin-left: 0.625rem;
      }

      .order-heading {
        font-size: 1.25rem;
      }

      &__section-title {
        padding: 0 0.625rem 0.5rem;
      }

      &__section-content {
        --sf-tile-padding: 1rem;
      }

      &__column--left {
        gap: 1.5rem;
      }

      &__tile {
        padding-top: 1rem;
        padding-bottom: 1rem;
      }

      &__heading {
        font-size: 25px;
        margin-top: 0;
        padding-left: 10px;
      }

      &__summary {
        gap: 0.5rem;

        :deep(.tile__outer) {
          padding-top: 0;
        }
      }
    }
  }
</style>
